import { getAuthHeaders } from "../lib/auth";
import { useState } from "react";
import PharmatechLayout from "../components/PharmatechLayout";
import {
  Users, Plus, X, AlertTriangle, CheckCircle2, AlertCircle,
  Shield, Loader2, User, Pill, Baby, Brain
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const CONDITIONS_LIST = ["grossesse", "allaitement", "insuffisance_renale_severe", "insuffisance_hepatique_severe", "ulcere_gastroduodenal", "asthme", "diabete", "epilepsie", "glaucome", "HTA"];
const FEU_CONFIG = {
  vert: { color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0", label: "Simulation favorable", icon: CheckCircle2 },
  orange: { color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", label: "Risques détectés - Prudence", icon: AlertTriangle },
  rouge: { color: "#ef4444", bg: "#fef2f2", border: "#fecaca", label: "Simulation defavorable - Danger", icon: AlertCircle },
};

export default function ProfilPatientPage() {
  const [patient, setPatient] = useState({ age: "", sexe: "", poids_kg: "", grossesse: false, trimestre: "", allaitement: false, conditions: [], medications_actuelles: [] });
  const [medInput, setMedInput] = useState("");
  const [newDrugs, setNewDrugs] = useState([{ name: "", dosage: "" }]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const addMed = () => { if (medInput.trim()) { setPatient(p => ({ ...p, medications_actuelles: [...p.medications_actuelles, medInput.trim()] })); setMedInput(""); } };
  const toggleCondition = (c) => setPatient(p => ({ ...p, conditions: p.conditions.includes(c) ? p.conditions.filter(x => x !== c) : [...p.conditions, c] }));

  const simulate = async () => {
    const valid = newDrugs.filter(d => d.name.trim());
    if (!valid.length) { toast.error("Ajoutez au moins un nouveau médicament"); return; }
    setLoading(true); setResult(null);
    try {
      const res = await axios.post(`${API}/pharmatech/digital-twin/simulate`, {
        patient: { age: patient.age ? parseInt(patient.age) : null, sexe: patient.sexe || null, poids_kg: patient.poids_kg ? parseFloat(patient.poids_kg) : null, grossesse: patient.grossesse, trimestre: patient.trimestre ? parseInt(patient.trimestre) : null, allaitement: patient.allaitement, conditions: patient.conditions, medications_actuelles: patient.medications_actuelles },
        new_drugs: valid.map(d => ({ name: d.name, dosage: d.dosage })),
      }, { headers: getHeaders() });
      setResult(res.data);
    } catch (e) { toast.error("Erreur: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  const feu = result ? FEU_CONFIG[result.feu] : null;

  return (
    <PharmatechLayout title="Profil Patient Virtuel" subtitle="Simulation d'impact thérapeutique personnalisée">
      <div data-testid="profil-patient-page" className="max-w-4xl mx-auto space-y-6">
        {/* Patient Profile */}
        <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-blue-500" /> Profil du patient
          </h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Age</label>
              <input data-testid="twin-age" type="number" value={patient.age} onChange={e => setPatient(p => ({...p, age: e.target.value}))}
                className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50" placeholder="65" /></div>
            <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Sexe</label>
              <select data-testid="twin-sexe" value={patient.sexe} onChange={e => setPatient(p => ({...p, sexe: e.target.value}))}
                className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50"><option value="">--</option><option value="M">Masculin</option><option value="F">Feminin</option></select></div>
            <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Poids (kg)</label>
              <input data-testid="twin-weight" type="number" value={patient.poids_kg} onChange={e => setPatient(p => ({...p, poids_kg: e.target.value}))}
                className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50" placeholder="70" /></div>
          </div>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={patient.grossesse} onChange={e => setPatient(p => ({...p, grossesse: e.target.checked}))} className="rounded" data-testid="twin-grossesse" />
              <Baby className="w-3.5 h-3.5 text-pink-500" /> Grossesse
            </label>
            {patient.grossesse && <select value={patient.trimestre} onChange={e => setPatient(p => ({...p, trimestre: e.target.value}))} className="px-2 py-1 rounded text-xs border border-slate-200 bg-slate-50"><option value="">Trimestre?</option><option value="1">T1</option><option value="2">T2</option><option value="3">T3</option></select>}
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={patient.allaitement} onChange={e => setPatient(p => ({...p, allaitement: e.target.checked}))} className="rounded" data-testid="twin-allaitement" /> Allaitement
            </label>
          </div>
          <div className="mb-4"><label className="text-[10px] text-slate-500 uppercase mb-2 block">Pathologies existantes</label>
            <div className="flex flex-wrap gap-2">
              {CONDITIONS_LIST.map(c => (
                <button key={c} onClick={() => toggleCondition(c)} data-testid={`twin-cond-${c}`}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all border ${patient.conditions.includes(c) ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                  {c.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase mb-2 block flex items-center gap-1"><Pill className="w-3 h-3" /> Traitement actuel</label>
            <div className="flex gap-2">
              <input value={medInput} onChange={e => setMedInput(e.target.value)} onKeyDown={e => e.key === "Enter" && addMed()}
                className="flex-1 px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50" placeholder="Ex: Metformine, Amlodipine..." data-testid="twin-med-input" />
              <Button onClick={addMed} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="twin-add-med-btn">+</Button>
            </div>
            {patient.medications_actuelles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {patient.medications_actuelles.map((m, i) => (
                  <span key={i} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-200">
                    <Pill className="w-2.5 h-2.5" />{m}
                    <button onClick={() => setPatient(p => ({...p, medications_actuelles: p.medications_actuelles.filter((_,j) => j !== i)}))} className="hover:text-red-500 ml-0.5"><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* New drugs */}
        <div className="p-5 rounded-xl bg-white border border-green-200 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Pill className="w-4 h-4 text-green-500" /> Nouveaux médicaments à tester
          </h3>
          <div className="space-y-2">
            {newDrugs.map((drug, idx) => (
              <div key={idx} className="flex gap-2">
                <input data-testid={`new-drug-${idx}`} value={drug.name}
                  onChange={e => { const u = [...newDrugs]; u[idx].name = e.target.value; setNewDrugs(u); }}
                  className="flex-1 px-3 py-2.5 rounded-lg text-sm border border-slate-200 bg-slate-50 placeholder-slate-400"
                  placeholder="Nom du médicament (DCI ou commercial)..." />
                <input value={drug.dosage} onChange={e => { const u = [...newDrugs]; u[idx].dosage = e.target.value; setNewDrugs(u); }}
                  className="w-28 px-3 py-2.5 rounded-lg text-sm border border-slate-200 bg-slate-50 placeholder-slate-400" placeholder="Dosage" />
                {newDrugs.length > 1 && <button onClick={() => setNewDrugs(newDrugs.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
          <button onClick={() => setNewDrugs([...newDrugs, { name: "", dosage: "" }])} data-testid="twin-add-drug-btn"
            className="mt-3 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-green-700 bg-green-50 hover:bg-green-100 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>

        <Button onClick={simulate} disabled={loading} data-testid="simulate-btn" className="w-full py-3 text-sm font-bold rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Simulation en cours...</> : <><Brain className="w-4 h-4 mr-2" /> Lancer la simulation</>}
        </Button>

        {result && (
          <div className="space-y-4" data-testid="twin-results">
            <div className="p-5 rounded-xl" style={{ background: feu.bg, border: `2px solid ${feu.border}` }}>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `${feu.color}15` }}>
                  <feu.icon className="w-7 h-7" style={{ color: feu.color }} />
                </div>
                <div className="flex-1">
                  <p className="text-lg font-bold" style={{ color: feu.color }}>{feu.label}</p>
                  <p className="text-xs mt-1 text-slate-600">Score: <strong style={{ color: feu.color }}>{result.risk_score}/100</strong> | {result.recommendation}</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
              <h4 className="text-xs font-bold text-slate-800 mb-2 flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-blue-500" /> Resume patient</h4>
              <div className="flex flex-wrap gap-2 text-[10px]">
                {result.patient_summary.age && <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">{result.patient_summary.age} ans</span>}
                {result.patient_summary.sexe && <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">{result.patient_summary.sexe === "M" ? "Homme" : "Femme"}</span>}
                {result.patient_summary.poids_kg && <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">{result.patient_summary.poids_kg} kg</span>}
                {result.patient_summary.grossesse && <span className="px-2 py-0.5 rounded bg-pink-50 text-pink-600 border border-pink-200">Enceinte</span>}
                {result.patient_summary.allaitement && <span className="px-2 py-0.5 rounded bg-pink-50 text-pink-600 border border-pink-200">Allaitante</span>}
                {result.patient_summary.conditions.map(c => <span key={c} className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">{c.replace(/_/g, " ")}</span>)}
                {result.patient_summary.medications_actuelles.map(m => <span key={m} className="px-2 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200">{m}</span>)}
              </div>
            </div>

            {result.interactions.length > 0 && (
              <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Interactions avec le traitement actuel</h4>
                <div className="space-y-2">
                  {result.interactions.map((inter, i) => (
                    <div key={i} className="p-3 rounded-lg" style={{ background: inter.gravite === "majeure" ? "#fef2f2" : "#fffbeb", border: `1px solid ${inter.gravite === "majeure" ? "#fecaca" : "#fde68a"}` }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase" style={{ background: inter.gravite === "majeure" ? "#fee2e2" : "#fef3c7", color: inter.gravite === "majeure" ? "#ef4444" : "#d97706" }}>{inter.gravite}</span>
                        <span className="text-xs font-bold text-slate-800">{inter.pair}</span>
                      </div>
                      <p className="text-xs text-slate-600">{inter.description}</p>
                      {inter.conduite && <p className="text-xs mt-1 font-medium text-blue-700">Conduite: {inter.conduite}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.contraindications.length > 0 && (
              <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-500" /> Contre-indications</h4>
                <div className="space-y-2">
                  {result.contraindications.map((ci, i) => (
                    <div key={i} className="p-3 rounded-lg bg-red-50 border border-red-200">
                      <p className="text-xs text-slate-800 font-medium">{ci.drug} | {ci.condition}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{ci.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.ai_simulation && (
              <div className="p-5 rounded-xl bg-blue-50 border border-blue-200">
                <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Brain className="w-4 h-4 text-blue-500" /> Analyse IA du profil</h4>
                <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{result.ai_simulation}</p>
              </div>
            )}

            {result.interactions.length === 0 && result.contraindications.length === 0 && (
              <div className="p-4 rounded-xl text-center bg-green-50 border border-green-200">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm font-medium text-slate-800">Aucune interaction ni contre-indication détectée</p>
                <p className="text-xs text-slate-500 mt-1">La combinaison semble compatible avec le profil patient</p>
              </div>
            )}

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-[10px] leading-relaxed text-amber-700"><Shield className="w-3 h-3 inline mr-1" />{result.disclaimer}</p>
            </div>
          </div>
        )}
      </div>
    </PharmatechLayout>
  );
}
